'use client';

import { useState } from 'react';
import RegionCard from './RegionCard';
import FestivalCard from './FestivalCard';
import CreatorSpotlight from './CreatorSpotlight';
import LanguageSelector from './LanguageSelector';
import RegionalStats from './RegionalStats';
import InteractiveMap from './InteractiveMap';
import Icon from '@/components/ui/AppIcon';

interface Region {
  id: string;
  name: string;
  language: string;
  image: string;
  alt: string;
  storiesCount: number;
  creatorsCount: number;
  popularGenres: string[];
  culturalHighlight: string;
}

interface Festival {
  name: string;
  date: string;
  region: string;
  image: string;
  alt: string;
  storiesCount: number;
  description: string;
}

interface Creator {
  name: string;
  region: string;
  language: string;
  image: string;
  alt: string;
  storiesCount: number;
  followers: number;
  specialty: string;
  quote: string;
}

interface Stat {
  label: string;
  value: string;
  icon: string;
  color: string;
}

export default function RegionalHubsInteractive() {
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [activeFilter, setActiveFilter] = useState('all');

  const regions: Region[] = [
  {
    id: 'maharashtra',
    name: 'Maharashtra',
    language: 'Marathi',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e335a60b-1766998687720.png",
    alt: 'Vibrant orange and yellow marigold flowers at traditional Maharashtrian festival celebration',
    storiesCount: 12500,
    creatorsCount: 450,
    popularGenres: ['Folk Tales', 'Historical', 'Romance'],
    culturalHighlight: 'Home to Lavani folk traditions and Marathi theatre heritage'
  },
  {
    id: 'tamil-nadu',
    name: 'Tamil Nadu',
    language: 'Tamil',
    image: "https://images.unsplash.com/photo-1701068156780-3acbc0597049",
    alt: 'Ancient stone temple with intricate carvings and colorful gopuram tower in Tamil Nadu',
    storiesCount: 15200,
    creatorsCount: 520,
    popularGenres: ['Classical', 'Mythology', 'Drama'],
    culturalHighlight: 'Rich Sangam literature and classical Tamil storytelling traditions'
  },
  {
    id: 'west-bengal',
    name: 'West Bengal',
    language: 'Bengali',
    image: "https://images.unsplash.com/photo-1603602612488-e8370535c00a",
    alt: 'Elaborate Durga Puja pandal with golden decorations and red drapes in Kolkata',
    storiesCount: 14800,
    creatorsCount: 480,
    popularGenres: ['Poetry', 'Social Drama', 'Mystery'],
    culturalHighlight: 'Legacy of Rabindranath Tagore and Bengali literary renaissance'
  },
  {
    id: 'punjab',
    name: 'Punjab',
    language: 'Punjabi',
    image: "https://images.unsplash.com/photo-1585726618040-19ab4ddd7eaa",
    alt: 'Golden wheat fields with traditional Punjabi farmer in colorful turban at sunset',
    storiesCount: 9800,
    creatorsCount: 320,
    popularGenres: ['Folk Music', 'Rural Life', 'Comedy'],
    culturalHighlight: 'Vibrant Bhangra culture and Sufi poetry traditions'
  },
  {
    id: 'kerala',
    name: 'Kerala',
    language: 'Malayalam',
    image: "https://images.unsplash.com/photo-1667480404784-500ba8399bc9",
    alt: 'Lush green backwaters with traditional wooden houseboat and coconut palm trees in Kerala',
    storiesCount: 11200,
    creatorsCount: 380,
    popularGenres: ['Nature Stories', 'Social Issues', 'Thriller'],
    culturalHighlight: 'Kathakali dance-drama and Malayalam literary excellence'
  },
  {
    id: 'gujarat',
    name: 'Gujarat',
    language: 'Gujarati',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14b439238-1764785215543.png",
    alt: 'Colorful traditional Gujarati garba dancers in mirror-work chaniya choli during Navratri',
    storiesCount: 8900,
    creatorsCount: 290,
    popularGenres: ['Business Tales', 'Folk', 'Inspirational'],
    culturalHighlight: 'Garba traditions and entrepreneurial storytelling heritage'
  },
  {
    id: 'rajasthan',
    name: 'Rajasthan',
    language: 'Hindi/Rajasthani',
    image: "https://images.unsplash.com/photo-1731202484768-34a1047487eb",
    alt: 'Majestic sandstone fort with intricate jharokha windows against blue sky in Rajasthan',
    storiesCount: 10500,
    creatorsCount: 350,
    popularGenres: ['Historical', 'Royal Tales', 'Folk Music'],
    culturalHighlight: 'Legendary tales of valor and Rajasthani folk ballads'
  },
  {
    id: 'karnataka',
    name: 'Karnataka',
    language: 'Kannada',
    image: "https://images.unsplash.com/photo-1664621901312-59216cf972de",
    alt: 'Ancient Hoysala temple with detailed stone sculptures and pillars in Karnataka',
    storiesCount: 10800,
    creatorsCount: 360,
    popularGenres: ['Classical', 'Tech Stories', 'Philosophy'],
    culturalHighlight: 'Carnatic music heritage and modern tech culture blend'
  }];


  const festivals: Festival[] = [
  {
    name: 'Pongal',
    date: '14-17 Jan',
    region: 'Tamil Nadu',
    image: "https://images.unsplash.com/photo-1705489529622-e4a45b1c3283",
    alt: 'Traditional clay pot with colorful kolam rangoli patterns and sugarcane decorations for Pongal',
    storiesCount: 250,
    description: 'Harvest festival celebrating prosperity with traditional stories and folk songs'
  },
  {
    name: 'Durga Puja',
    date: '20-24 Oct',
    region: 'West Bengal',
    image: "https://images.unsplash.com/photo-1698128716138-69048cd5c413",
    alt: 'Illuminated Durga idol with red and gold decorations in elaborate pandal during evening aarti',
    storiesCount: 420,
    description: 'Grand celebration with mythological narratives and cultural performances'
  },
  {
    name: 'Onam',
    date: '20-31 Aug',
    region: 'Kerala',
    image: "https://images.unsplash.com/photo-1607076649379-65a1a893207c",
    alt: 'Circular pookalam flower arrangement with yellow and orange petals in traditional Kerala pattern',
    storiesCount: 310,
    description: 'Harvest festival featuring Mahabali legends and traditional boat races'
  },
  {
    name: 'Navratri',
    date: '15-23 Oct',
    region: 'Gujarat',
    image: "https://images.unsplash.com/photo-1708026477826-6b0ae5edad9e",
    alt: 'Women in colorful chaniya choli performing garba dance with dandiya sticks at night',
    storiesCount: 380,
    description: 'Nine nights of devotion with Garba dances and goddess worship stories'
  }];


  const creators: Creator[] = [
  {
    name: 'Priya Sharma',
    region: 'Maharashtra',
    language: 'Marathi',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_111e29bbe-1763294966477.png",
    alt: 'Indian woman with long black hair in traditional orange saree smiling warmly at camera',
    storiesCount: 85,
    followers: 45000,
    specialty: 'Folk Tales & Historical Fiction',
    quote: 'Every village in Maharashtra has a story waiting to be told'
  },
  {
    name: 'Arjun Menon',
    region: 'Kerala',
    language: 'Malayalam',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aca0fbf3-1763293623203.png",
    alt: 'Young Indian man with short black hair in white kurta with confident expression',
    storiesCount: 72,
    followers: 38000,
    specialty: 'Nature Stories & Thrillers',
    quote: 'The backwaters whisper tales that only the heart can hear'
  },
  {
    name: 'Anjali Patel',
    region: 'Gujarat',
    language: 'Gujarati',
    image: "https://images.unsplash.com/photo-1529123202249-4f6224196c9b",
    alt: 'Indian woman with curly hair in green traditional dress with bright smile outdoors',
    storiesCount: 68,
    followers: 42000,
    specialty: 'Business Tales & Inspirational',
    quote: 'From street vendors to CEOs, Gujarat teaches us entrepreneurship through stories'
  }];


  const stats: Stat[] = [
  {
    label: 'Regional Stories',
    value: '95K+',
    icon: 'BookOpenIcon',
    color: 'bg-primary'
  },
  {
    label: 'Languages',
    value: '22+',
    icon: 'LanguageIcon',
    color: 'bg-secondary'
  },
  {
    label: 'Local Creators',
    value: '3.2K+',
    icon: 'UserGroupIcon',
    color: 'bg-accent'
  },
  {
    label: 'Cultural Events',
    value: '150+',
    icon: 'SparklesIcon',
    color: 'bg-success'
  }];


  const mapRegions = regions.map((r) => ({
    id: r.id,
    name: r.name,
    storiesCount: r.storiesCount
  }));

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
  };

  const handleRegionSelect = (regionId: string) => {
    const element = document.getElementById(`region-${regionId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const filterOptions = [
  { id: 'all', label: 'All Regions', icon: 'GlobeAltIcon' },
  { id: 'popular', label: 'Most Popular', icon: 'FireIcon' },
  { id: 'new', label: 'New Content', icon: 'SparklesIcon' }];


  return (
    <div className="min-h-screen bg-background">
      <div className="relative bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary mb-4">
                Discover Regional Stories
              </h1>
              <p className="text-lg text-text-secondary max-w-2xl">
                Explore India's rich cultural tapestry through authentic stories in your language
              </p>
            </div>
            <LanguageSelector onLanguageChange={handleLanguageChange} />
          </div>

          <RegionalStats stats={stats} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <InteractiveMap regions={mapRegions} onRegionSelect={handleRegionSelect} />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-heading font-bold text-text-primary">
            Regional Hubs
          </h2>
          <div className="flex items-center space-x-2">
            {filterOptions.map((option) =>
            <button
              key={option.id}
              onClick={() => setActiveFilter(option.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
              activeFilter === option.id ?
              'bg-primary text-primary-foreground shadow-warm' :
              'bg-card text-text-primary hover:bg-muted'}`
              }>

                <Icon name={option.icon as any} size={18} />
                <span className="hidden md:inline">{option.label}</span>
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {regions.map((region) =>
          <div key={region.id} id={`region-${region.id}`}>
              <RegionCard region={region} />
            </div>
          )}
        </div>
      </div>

      <div className="bg-gradient-to-br from-accent/10 to-secondary/10 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">
                Cultural Festivals
              </h2>
              <p className="text-text-secondary">
                Celebrate India's diversity through festival stories
              </p>
            </div>
            <Icon name="SparklesIcon" size={32} className="text-accent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {festivals.map((festival, index) =>
            <FestivalCard key={index} festival={festival} />
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">
              Featured Regional Creators
            </h2>
            <p className="text-text-secondary">
              Meet the voices bringing local stories to life
            </p>
          </div>
          <Icon name="MicrophoneIcon" size={32} className="text-primary" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {creators.map((creator, index) =>
          <CreatorSpotlight key={index} creator={creator} />
          )}
        </div>
      </div>

      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <Icon name="HeartIcon" size={48} className="text-primary mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
            Your Culture, Your Language, Your Stories
          </h2>
          <p className="text-lg text-text-secondary mb-8">
            Join millions discovering authentic regional content on KissaFM
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button className="flex items-center space-x-2 px-8 py-4 bg-cta text-cta-foreground rounded-lg font-cta font-bold text-lg transition-all duration-300 hover:bg-cta/90 hover:shadow-warm-lg">
              <Icon name="ArrowDownTrayIcon" size={24} />
              <span>Download App</span>
            </button>
            <button className="flex items-center space-x-2 px-8 py-4 bg-card text-text-primary rounded-lg font-cta font-bold text-lg border-2 border-primary transition-all duration-300 hover:bg-primary hover:text-primary-foreground">
              <Icon name="PlayIcon" size={24} />
              <span>Explore Stories</span>
            </button>
          </div>
        </div>
      </div>
    </div>);

}